import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";

export default function VariablesPage() {
  const [variables, setVariables] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<number | null>(null);
  const [editData, setEditData] = useState({ name: "", value: "", type: "text" });
  const [showNew, setShowNew] = useState(false);
  const [newData, setNewData] = useState({ name: "", value: "", type: "text" });

  useEffect(() => {
    api.get("/api/v1/variables").then(setVariables).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!newData.name) return;
    try {
      const result = await api.post("/api/v1/variables", newData);
      setVariables([{ id: result.id, ...newData }, ...variables]);
      setNewData({ name: "", value: "", type: "text" });
      setShowNew(false);
      toast.success("Variable created");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleUpdate = async (id: number) => {
    try {
      await api.put(`/api/v1/variables/${id}`, editData);
      setVariables(variables.map((v) => v.id === id ? { ...v, ...editData } : v));
      setEditing(null);
      toast.success("Updated");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this variable?")) return;
    try {
      await api.delete(`/api/v1/variables/${id}`);
      setVariables(variables.filter((v) => v.id !== id));
      toast.success("Deleted");
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Variables</h1>
        <button onClick={() => setShowNew(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Variable
        </button>
      </div>
      {showNew && (
        <div className="bg-card rounded-xl border border-primary/30 p-4 mb-4 flex items-center gap-3">
          <input value={newData.name} onChange={(e) => setNewData({ ...newData, name: e.target.value })} placeholder="Name" className="w-40 px-3 py-1.5 rounded-lg border border-input bg-background text-sm" />
          <input value={newData.value} onChange={(e) => setNewData({ ...newData, value: e.target.value })} placeholder="Value" className="flex-1 px-3 py-1.5 rounded-lg border border-input bg-background text-sm" />
          <button onClick={handleCreate} className="p-1.5 rounded-lg bg-primary text-primary-foreground"><Check className="w-4 h-4" /></button>
          <button onClick={() => setShowNew(false)} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
      )}
      {loading ? <div className="animate-pulse space-y-3">{[1,2,3].map(i=><div key={i} className="h-12 bg-muted rounded-lg"/>)}</div> : (
        <div className="space-y-2">
          {variables.map((v) => (
            <div key={v.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
              {editing === v.id ? (
                <>
                  <input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="w-40 px-3 py-1.5 rounded-lg border border-input bg-background text-sm" />
                  <input value={editData.value} onChange={(e) => setEditData({ ...editData, value: e.target.value })} className="flex-1 px-3 py-1.5 rounded-lg border border-input bg-background text-sm" />
                  <button onClick={() => handleUpdate(v.id)} className="p-1.5 rounded-lg bg-primary text-primary-foreground"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium w-40 shrink-0 truncate">{v.name}</span>
                  <span className="flex-1 text-sm text-muted-foreground truncate">{v.value || "—"}</span>
                  <button onClick={() => { setEditing(v.id); setEditData({ name: v.name, value: v.value || "", type: v.type || "text" }); }} className="p-1.5 rounded hover:bg-accent"><Pencil className="w-4 h-4 text-muted-foreground" /></button>
                  <button onClick={() => handleDelete(v.id)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
